// Shared criteria × levels matrix — v4 recompose of the APL-11550 spec's
// src/pages/student-growth/components/RubricMatrix.tsx (c1a4cbeb; there it
// was lifted from experiences' InfoModal so the growth "View rubric" modal
// and assessment-details modal share one renderer). Ordering + label
// resolution ported verbatim; the .scss table recomposed on Tailwind.
// Consumers today: the custom-rubric library preview (CR3); the deferred
// student-growth module reuses it when it lands.
import { orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';

export interface RubricMatrixLevel {
  id?: number | string | null;
  label?: string | null;
  level_label?: string | null;
  desc?: string | null;
}

export interface RubricMatrixCriterion {
  id?: number | string | null;
  title?: string | null;
  custom_fields?: { title?: string | null } | null;
  levels_json?: RubricMatrixLevel[] | null;
}

interface RubricMatrixProps {
  criteria: RubricMatrixCriterion[] | null | undefined;
  'data-testid'?: string;
}

// Falls back through label → level_label → `L{ordinal}` (spec verbatim).
export const resolveLevelLabel = (
  level: RubricMatrixLevel | null | undefined,
  position: number
): string => {
  if (level?.label != null && String(level.label).trim() !== '') {
    return String(level.label);
  }
  if (level?.level_label != null && String(level.level_label).trim() !== '') {
    return String(level.level_label);
  }
  return `L${level?.id ?? position + 1}`;
};

// Callers gate the preview affordance on this (no levels → nothing to show).
export const hasRubricMatrix = (criteria: RubricMatrixCriterion[] | null | undefined): boolean =>
  Array.isArray(criteria) &&
  criteria.some((c) => Array.isArray(c?.levels_json) && c.levels_json.length > 0);

export const RubricMatrix = ({ criteria, 'data-testid': testId }: RubricMatrixProps) => {
  const { t } = useTranslation();

  const list: RubricMatrixCriterion[] = Array.isArray(criteria) ? criteria : [];
  const criteriaWithLevels = list.filter(
    (criterion) => Array.isArray(criterion?.levels_json) && (criterion.levels_json?.length ?? 0) > 0
  );

  if (criteriaWithLevels.length === 0) return null;

  // Re-sort by id (BE's `default_scope { order(title: :asc) }` returns them
  // alphabetical) to recover authored order. Copy before sort (spec verbatim).
  const orderedCriteria = [...criteriaWithLevels].sort((a, b) => Number(a?.id) - Number(b?.id));
  const matrixRows = orderedCriteria.map((criterion) => ({
    id: criterion?.id,
    title: criterion?.title || criterion?.custom_fields?.title || '',
    levels: orderBy(
      criterion.levels_json ?? [],
      [(level: RubricMatrixLevel) => Number(level?.id) || 0],
      ['asc']
    ),
  }));
  const maxLevelCount = matrixRows.reduce((max, row) => Math.max(max, row.levels.length), 0);
  // Per-column header: first criterion with a level at that position wins,
  // else a `Level N` fallback so ragged rubrics still render.
  const columnHeaders = Array.from({ length: maxLevelCount }, (_unused, columnIndex) => {
    const sourceRow = matrixRows.find((row) => row.levels[columnIndex] != null);
    return sourceRow
      ? resolveLevelLabel(sourceRow.levels[columnIndex], columnIndex)
      : `${t('CommonText.level')} ${columnIndex + 1}`;
  });

  return (
    <div className="overflow-x-auto rounded-lg border" data-testid={testId ?? 'rubric-matrix'}>
      <table className="w-full min-w-fit border-collapse text-sm">
        <thead>
          <tr className="border-b bg-ap-muted-a50">
            <th className="border-e p-2 text-start font-medium">{t('CommonText.criteria')}</th>
            {columnHeaders.map((header, columnIndex) => (
              <th
                className="border-e p-2 text-start font-medium last:border-e-0"
                key={`rubric-col-${columnIndex}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrixRows.map((row, rowIndex) => (
            <tr key={`rubric-row-${row.id ?? rowIndex}`} className="border-b last:border-b-0">
              <th className="border-e p-2 text-start align-top font-semibold" scope="row">
                {row.title}
              </th>
              {columnHeaders.map((_header, columnIndex) => {
                const level = row.levels[columnIndex];
                const cellKey = `rubric-cell-${row.id ?? rowIndex}-${columnIndex}`;
                if (level == null) {
                  return <td className="border-e p-2 align-top last:border-e-0" key={cellKey} />;
                }
                const hasDesc = level?.desc != null && String(level.desc).trim() !== '';
                return (
                  <td className="border-e p-2 align-top last:border-e-0" key={cellKey}>
                    <div className="font-medium">{resolveLevelLabel(level, columnIndex)}</div>
                    {hasDesc && <div className="mt-1 text-muted-foreground">{level.desc}</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
